/**
 * City page geography: listings located in the city itself vs listings from
 * the surrounding county / nearby localities. Shared by the route loader
 * (status, robots, counts) and the page (labels, sections) so both agree.
 */

type Loc = { location_city?: string | null; location_region?: string | null; status?: string | null; category_code?: string | null };

// City pages promise "Mașini de vânzare": only passenger cars (cars + SUV),
// only active listings (the public list RPC also returns sold ones).
const CAR_CODES = new Set(["cars", "suv"]);
export const isEligibleCityListing = (l: Loc) =>
  (l.status == null || l.status === "active") && (l.category_code == null || CAR_CODES.has(l.category_code));

export const norm = (s: string) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const slugifyPlace = (s: string) => norm(s).replace(/ /g, "-");

// County seats: the county's other listings are shown as alternatives.
export const COUNTY_SEATS: Record<string, string> = {
  "cluj-napoca": "cluj", sibiu: "sibiu", brasov: "brasov", constanta: "constanta", timisoara: "timis",
  iasi: "iasi", bucuresti: "bucuresti", oradea: "bihor", craiova: "dolj", ploiesti: "prahova",
  galati: "galati", braila: "braila", arad: "arad", pitesti: "arges", bacau: "bacau",
  "targu-mures": "mures", "baia-mare": "maramures", buzau: "buzau", "satu-mare": "satu mare",
  botosani: "botosani", "piatra-neamt": "neamt", "ramnicu-valcea": "valcea", suceava: "suceava",
  "drobeta-turnu-severin": "mehedinti", targoviste: "dambovita", focsani: "vrancea", tulcea: "tulcea",
  resita: "caras severin", slatina: "olt", calarasi: "calarasi", giurgiu: "giurgiu", "alba-iulia": "alba",
  deva: "hunedoara", zalau: "salaj", bistrita: "bistrita nasaud", "sfantu-gheorghe": "covasna",
  "miercurea-ciuc": "harghita", vaslui: "vaslui", slobozia: "ialomita", alexandria: "teleorman",
};

const EXTRA_COUNTIES: Record<string, string[]> = { bucuresti: ["ilfov"] };

/** True when the listing is located in the city itself (not just its county). */
export function isInCity(l: Loc, citySlug: string, cityName: string): boolean {
  const c = norm(l.location_city || "");
  if (!c) return false;
  return c === norm(cityName) || c.replace(/ /g, "-") === citySlug;
}

export function splitCityListings<T extends Loc>(
  listings: T[] | undefined | null,
  citySlug: string,
  cityName: string,
): { city: T[]; area: T[]; countyName: string | null } {
  const city: T[] = [];
  const area: T[] = [];
  const county = COUNTY_SEATS[citySlug] ?? null;
  const counties = [...(county ? [county] : []), ...(EXTRA_COUNTIES[citySlug] || [])];
  for (const l of listings || []) {
    if (!isEligibleCityListing(l)) continue;
    if (isInCity(l, citySlug, cityName)) {
      city.push(l);
      continue;
    }
    const region = norm(l.location_region || "");
    // County only: we do not claim other localities are "near" the city.
    if (counties.length && region && counties.some((c) => region === c || region.startsWith(`${c} `))) area.push(l);
  }
  return { city, area, countyName: county };
}

/** A locality is real if it is in our city dataset or at least one listing names it. */
export function isKnownLocality(citySlug: string, knownSlugs: string[], listings: Loc[] | undefined | null): boolean {
  if (knownSlugs.includes(citySlug)) return true;
  return (listings || []).some((l) => l.location_city && slugifyPlace(l.location_city) === citySlug);
}

/** Distinct locality names of the area listings, most listings first. */
export function topPlaces(listings: Loc[], max = 3): string[] {
  const counts = new Map<string, { name: string; n: number }>();
  for (const l of listings) {
    const name = (l.location_city || "").trim();
    if (!name) continue;
    const key = norm(name);
    const cur = counts.get(key);
    // Prefer the spelling with diacritics when variants exist.
    const better = cur && /[ăâîșțşţ]/i.test(cur.name) ? cur.name : name;
    counts.set(key, { name: better, n: (cur?.n ?? 0) + 1 });
  }
  return [...counts.values()].sort((a, b) => b.n - a.n).slice(0, max).map((v) => v.name);
}

/** Copy aligned with the real geography of the results (Romanian pages). */
export function cityCopyRO(o: { cityName: string; county?: string | null; cityCount: number; areaCount: number; areaPlaces: string[] }) {
  const { cityName, county, cityCount, areaCount, areaPlaces } = o;
  const countyLabel = county ? (norm(county) === "bucuresti" ? "București și Ilfov" : `județul ${county}`) : "județ";
  const places = areaPlaces.length ? ` (${areaPlaces.join(", ")})` : "";
  if (cityCount === 0 && areaCount > 0) {
    return {
      title: `Mașini de vânzare în ${countyLabel}`,
      h1: `Mașini de vânzare în ${countyLabel}`,
      description: `Acum nu avem anunțuri în ${cityName}. Vezi ${areaCount} anunțuri din ${countyLabel}${places}, cu localitatea fiecăreia.`,
      subtitle: `Acum nu avem anunțuri în ${cityName}. Mai jos sunt ${areaCount} anunțuri din ${countyLabel}${places}.`,
    };
  }
  if (areaCount > 0) {
    return {
      title: `Mașini de vânzare în ${cityName}`,
      h1: `Mașini de vânzare în ${cityName}`,
      description: `${cityCount} anunțuri auto în ${cityName} și încă ${areaCount} în ${countyLabel}${places}. Preț, kilometraj și contact direct.`,
      subtitle: `${cityCount} anunțuri în ${cityName}, plus ${areaCount} în ${countyLabel}, afișate separat.`,
    };
  }
  return {
    title: `Mașini de vânzare în ${cityName}`,
    h1: `Mașini de vânzare în ${cityName}`,
    description: cityCount > 0
      ? `${cityCount} anunțuri auto în ${cityName}, de la dealeri și persoane fizice. Preț, kilometraj și contact direct.`
      : `Acum nu avem anunțuri în ${cityName}. Caută în toată țara sau revino curând.`,
    subtitle: cityCount > 0
      ? `${cityCount} anunțuri în ${cityName}, de la dealeri și vânzători privați.`
      : `Acum nu avem anunțuri în ${cityName}.`,
  };
}

/**
 * Link text for a locality page that matches what the page shows: county seats
 * also list the rest of their county, so the label names the county too.
 */
const COUNTY_DISPLAY: Record<string, string> = {
  timis: "Timiș", iasi: "Iași", brasov: "Brașov", constanta: "Constanța", arges: "Argeș", bacau: "Bacău",
  mures: "Mureș", buzau: "Buzău", "satu mare": "Satu Mare", botosani: "Botoșani", neamt: "Neamț",
  valcea: "Vâlcea", mehedinti: "Mehedinți", dambovita: "Dâmbovița", "caras severin": "Caraș-Severin",
  calarasi: "Călărași", salaj: "Sălaj", "bistrita nasaud": "Bistrița-Năsăud", galati: "Galați",
  braila: "Brăila", ialomita: "Ialomița",
};

export function cityLinkLabel(citySlug: string, cityName: string, countyName?: string | null): string {
  const county = COUNTY_SEATS[citySlug];
  if (!county) return cityName;
  if (citySlug === "bucuresti") return `${cityName} și Ilfov`;
  const display = countyName || COUNTY_DISPLAY[county] || county.replace(/\b\w/g, (c) => c.toUpperCase());
  return `${cityName} și județul ${display}`;
}
